import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import useAuthListener from '../../hooks/useAuthListener';
import { doc, getDoc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import wintoCoin from '../../assets/images/winto-coin.png';
import './WinisPurchaseSuccess.css';

const WinisPurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [purchaseDetails, setPurchaseDetails] = useState(null);
  const [winisUpdated, setWinisUpdated] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const { user, loading } = useAuthListener();
  const pdfRef = useRef(null);

  useEffect(() => {
    const amount = searchParams.get('amount');
    const winisAmount = searchParams.get('winisAmount');
    const currency = searchParams.get('currency');
    const userId = searchParams.get('userId');
    
    if (amount && winisAmount && currency) {
      setPurchaseDetails({
        amount: parseFloat(amount),
        winisAmount: parseInt(winisAmount),
        currency: currency,
        userId: userId
      });

      // Update winis if we have the data and not already updated
      if (winisAmount && !winisUpdated) {
        // Function to attempt winis update
        const attemptUpdate = () => {
          if (user && !winisUpdated) {
            updateWinis(parseInt(winisAmount));
          } else if (!loading && !user) {
            // Retry after 3 seconds if still not authenticated
            setTimeout(attemptUpdate, 3000);
          } else if (loading) {
            // Retry after 1 second if still loading
            setTimeout(attemptUpdate, 1000);
          }
        };

        // Start the update process
        attemptUpdate();
      }
    }
  }, [searchParams, user, winisUpdated, loading]);

  // Send email notification when purchase details are loaded
  useEffect(() => {
    if (purchaseDetails && user && !emailSent) {
      sendEmailNotification();
    }
  }, [purchaseDetails, user, emailSent]);

  const updateWinis = async (winisToAdd) => {
    try {
      // Check if we have a user from auth state
      if (!user) {
        throw new Error('User authentication required to update winis');
      }
      
      // Reference to user's wallet document (winicoins)
      const walletRef = doc(db, 'users', user.uid, 'wallet', 'winicoins');
      
      // Get current winicoins
      const walletDoc = await getDoc(walletRef);
      let currentWinicoins = 0;
      
      if (walletDoc.exists()) {
        currentWinicoins = walletDoc.data().winicoins || 0;
      }

      // Calculate new total
      const newTotal = currentWinicoins + winisToAdd;

      // Update or create the document
      await setDoc(walletRef, {
        winicoins: newTotal,
        lastUpdated: new Date(),
        purchaseHistory: arrayUnion({
          amountPaid: purchaseDetails.amount,
          winisPurchased: winisToAdd,
          currency: purchaseDetails.currency,
          timestamp: new Date()
        })
      }, { merge: true });

      setWinisUpdated(true);
    } catch (error) {
      console.error('Error updating winis:', error);
      setUpdateError(`Failed to update winis: ${error.message}. Please contact support.`);
    }
  };

  const handleBackToWallet = () => {
    navigate('/wallet');
  };

  const handleBuyMore = () => {
    navigate('/deposit');
  };

  const generatePDF = async () => {
    if (!pdfRef.current) return;
    
    setIsGeneratingPDF(true);
    try {
      const canvas = await html2canvas(pdfRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 0;
      
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      const fileName = `Winis_Purchase_Success_${purchaseDetails.winisAmount}_Winis_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const sendEmailNotification = async () => {
    if (!user || emailSent) return;
    
    try {
      // Get user email from Firestore
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userEmail = userDoc.data()?.email;
      
      if (!userEmail) {
        console.error('User email not found');
        return;
      }

      const emailData = {
        to: userEmail,
        subject: `Winis Purchase Success - ${purchaseDetails.winisAmount} Winis`,
        message: `
          <h2>🎉 Winis Purchase Successful!</h2>
          <p>Dear Valued Customer,</p>
          
          <p>Congratulations! Your Winis purchase has been successfully processed. Here are the details of your purchase:</p>
          
          <div style="background: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h3>Purchase Details:</h3>
            <ul>
              <li><strong>Amount Paid:</strong> ${purchaseDetails.amount.toFixed(2)} ${purchaseDetails.currency}</li>
              <li><strong>Winis Purchased:</strong> ${purchaseDetails.winisAmount} Winis</li>
              <li><strong>Exchange Rate:</strong> 1 Winis = ${(purchaseDetails.amount / purchaseDetails.winisAmount).toFixed(2)} ${purchaseDetails.currency}</li>
              <li><strong>Purchase Date:</strong> ${new Date().toLocaleDateString()}</li>
            </ul>
          </div>
          
          <p>Your Winis have been successfully added to your wallet and are ready to use for various services within the iWinto platform.</p>
          
          <p>Thank you for choosing iWinto!</p>
          
          <p>Best regards,<br>The iWinto Team</p>
        `
      };

      // Send email via FormSubmit
      const response = await fetch('https://formsubmit.co/ajax/your-email@example.com', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailData)
      });

      if (response.ok) {
        setEmailSent(true);
        console.log('Email notification sent successfully');
      } else {
        console.error('Failed to send email notification');
      }
    } catch (error) {
      console.error('Error sending email notification:', error);
    }
  };

  if (!purchaseDetails) {
    return (
      <div className="winis-purchase-success-page">
        <div className="winis-purchase-success-container">
          <div className="winis-purchase-success-loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="winis-purchase-success-page">
      <div className="winis-purchase-success-container" ref={pdfRef}>
        {/* PDF Download Button */}
        <button 
          className="winis-purchase-success-pdf-download-btn"
          onClick={generatePDF}
          disabled={isGeneratingPDF}
          title="Download as PDF"
        >
          {isGeneratingPDF ? (
            <span className="winis-purchase-success-pdf-loading">⏳</span>
          ) : (
            <span className="winis-purchase-success-pdf-icon">📄</span>
          )}
        </button>
        <div className="winis-purchase-success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22,4 12,14.01 9,11.01"></polyline>
          </svg>
        </div>
        
        <h1 className="winis-purchase-success-title">Thank You for Your Purchase!</h1>
        <p className="winis-purchase-success-subtitle">
          Your winis have been successfully purchased and added to your wallet.
        </p>

        <div className="winis-purchase-success-summary">
          <div className="winis-purchase-success-summary-item">
            <span className="winis-purchase-success-summary-label">Amount Paid:</span>
            <span className="winis-purchase-success-summary-value">{purchaseDetails.amount.toFixed(2)} {purchaseDetails.currency}</span>
          </div>
          <div className="winis-purchase-success-summary-item">
            <span className="winis-purchase-success-summary-label">Winis Purchased:</span>
            <span className="winis-purchase-success-summary-value winis-purchase-success-winis-value">
              <img src={wintoCoin} alt="Winicoin" className="winis-purchase-success-winicoin-icon" />
              {purchaseDetails.winisAmount} Winis
            </span>
          </div>
          <div className="winis-purchase-success-summary-item">
            <span className="winis-purchase-success-summary-label">Exchange Rate:</span>
            <span className="winis-purchase-success-summary-value winis-purchase-success-rate-value">
              1 Winis = {(purchaseDetails.amount / purchaseDetails.winisAmount).toFixed(2)} {purchaseDetails.currency}
            </span>
          </div>
        </div>

        <div className="winis-purchase-success-message">
          <p>
            You will receive a confirmation email shortly with all the details of your purchase. 
            Your winis are now available in your wallet and ready to use.
          </p>
          
          {winisUpdated && (
            <p className="winis-purchase-success-winis-success">
              ✅ Your winis have been successfully added to your wallet!
            </p>
          )}
          {updateError && (
            <div className="winis-purchase-success-winis-error">
              <p>⚠️ {updateError}</p>
              <button 
                className="winis-purchase-success-retry-winis-btn"
                onClick={() => {
                  setUpdateError(null);
                  setWinisUpdated(false);
                  if (purchaseDetails) {
                    updateWinis(purchaseDetails.winisAmount);
                  }
                }}
              >
                Retry Winis Update
              </button>
            </div>
          )}
        </div>

        <div className="winis-purchase-success-actions">
          <button 
            className="winis-purchase-success-btn-secondary"
            onClick={handleBackToWallet}
          >
            Go to Wallet
          </button>
          <button 
            className="winis-purchase-success-btn-primary"
            onClick={handleBuyMore}
          >
            Buy More Winis
          </button>
        </div>
      </div>
    </div>
  );
};

export default WinisPurchaseSuccess;
